import { ref } from 'vue';
import { formatToKST } from '@common/common/utils/timeUtils';
import type { LogUserInteractionsRequest, UserLogParameter } from '@common/openapi-client';
import useLogStore from '../stores/log';
import { logArr } from '../const/logDataList';
import type { CustomLogUserInteractionsRequest } from '../types/log';

interface UserInteractionLogParams {
    ScreenID: number;
    ObjectID: number;
    ActionID: number;
    Parameter: {
        Key: string;
        Value: string;
    };
}

/**
 * 사용자 상호작용 로그를 기록하는 composable
 *
 * @param isAdmin 관리자 여부 (관리자인 경우 로그를 기록하지 않음)
 *
 * @example
 * ```typescript
 * const { postUserInteractionLog, isLogging } = useUserInteractionLog(props.isAdmin);
 *
 * // 버튼 클릭 시 로그 기록
 * const handleButtonClick = () => {
 *     postUserInteractionLog({
 *         ScreenID: 100,
 *         ObjectID: 1,
 *         ActionID: 5,
 *         Parameter: {
 *             Key: 'button_click',
 *             Value: 'submit_button',
 *         },
 *     });
 * };
 * ```
 */
export const useUserInteractionLog = (isAdmin = false) => {
    const isLogging = ref(false);
    const logStore = useLogStore();

    const postUserInteractionLog = async (params: UserInteractionLogParams) => {
        // 관리자인 경우 로그를 기록하지 않음
        if (isAdmin) {
            return;
        }

        if (import.meta.env.MODE !== 'production') {
            return;
        }

        if (isLogging.value) return;

        try {
            isLogging.value = true;

            const logParams: LogUserInteractionsRequest = {
                ScreenID: params.ScreenID,
                ObjectID: params.ObjectID,
                ActionID: params.ActionID,
                Parameter: {
                    Key: params.Parameter.Key,
                    Value: params.Parameter.Value,
                } as UserLogParameter,
                TimeStamp: formatToKST(new Date()),
            };

            // TODO: 로그 전송 테스트 중 주석 처리
            await logStore.postUserInteractionLog(logParams);
        } catch (error) {
            console.error('Failed to post user interaction log:', error);
        } finally {
            isLogging.value = false;
        }
    };

    /**
     * 로그 타입 코드를 기반으로 사용자 상호작용 로그를 기록하는 함수
     *
     * @param screenID 화면 ID
     * @param logTypeCode 로그 타입 코드
     * @param value 로그 값 (선택적)
     *
     * @example
     * ```typescript
     * const { saveLog } = useUserInteractionLog(props.isAdmin);
     *
     * // 버튼 클릭 시 로그 기록
     * const handleButtonClick = () => {
     *     saveLog(161, 'CHANGE_EVALUATION_CLICK');
     * };
     *
     * // 값과 함께 로그 기록
     * const handleViewReport = (reportData: string) => {
     *     saveLog(161, 'VIEW_REPORT', reportData);
     * };
     * ```
     */
    const saveLog = (screenID: number, logTypeCode: string, value = '') => {
        // 관리자인 경우 로그를 기록하지 않음
        if (isAdmin) {
            return;
        }

        if (import.meta.env.MODE !== 'production') {
            return;
        }

        const log = logArr.find((log: CustomLogUserInteractionsRequest) => {
            if (log.ScreenID === screenID && log.LogTypeCode === logTypeCode) {
                return log;
            }
        });

        if (!log || log.ScreenID === undefined || log.ObjectID === undefined || log.ActionID === undefined) return;

        const params = {
            ScreenID: log.ScreenID,
            ObjectID: log.ObjectID,
            ActionID: log.ActionID,
            Parameter: {
                Key: log.Parameter?.Key || '',
                Value: value,
            },
        };

        postUserInteractionLog(params);
    };

    return {
        postUserInteractionLog,
        saveLog,
        isLogging,
    };
};
